import { useCallback, useMemo, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import moment, { type Moment } from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { useGasConsumption } from "../hooks/useGasConsumption";
import { GAS_UNIT, type GasRow } from "../models/GasConsumption";
import { GasConsumptionChart } from "./GasConsumptionChart";
import { GasConsumptionSummary } from "./GasConsumptionSummary";
import { GasConsumptionToolbar } from "./GasConsumptionToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const OUTER_LAYOUT = { grid: 38, chart: 62 };

const buildColumnDefs = (): ColDef<GasRow>[] => [
	{
		field: "Date",
		headerName: "Date",
		flex: 1,
		minWidth: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Value",
		headerName: `Gas Consumption (${GAS_UNIT})`,
		flex: 1,
		minWidth: 170,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => (p.value == null ? "" : Number(p.value).toFixed(2)),
	},
];

const ResizeGrip = ({ vertical = false }: { vertical?: boolean }) => (
	<Box
		sx={{
			width: vertical ? 48 : 8,
			height: vertical ? 6 : 48,
			borderRadius: 1,
			backgroundColor: "#e0e0e0",
			display: "flex",
			flexDirection: vertical ? "row" : "column",
			alignItems: "center",
			justifyContent: "center",
			gap: "3px",
			transition: "background-color 0.2s",
			"&:hover": {
				backgroundColor: "primary.main",
				"& .grip-dot": { backgroundColor: "#fff" },
			},
		}}
	>
		{[...Array(5)].map((_, i) => (
			<Box
				key={`grip-dot-${i}`}
				className="grip-dot"
				sx={{
					width: 4,
					height: 4,
					borderRadius: "50%",
					backgroundColor: "#9e9e9e",
					transition: "background-color 0.2s",
				}}
			/>
		))}
	</Box>
);

const URL_SCHEMA = {
	// Match the legacy default: Start = today - 3 days (CONS.GetNaturalGas
	// defaults @start to GETUTCDATE()-3), so the new page opens with the same
	// range as the legacy IHBox page.
	start: urlParam.date(() => moment().startOf("day").subtract(3, "days")),
	end: urlParam.date(() => moment().startOf("day")),
};

export const GasConsumptionReport = () => {
	const [{ start, end }, setFilters, { resetValues, isDefault }] =
		useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<GasRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useGasConsumption({
		start: startStr,
		end: endStr,
	});

	const outerLayout = useDefaultLayout({
		id: "gas-consumption-outer-v1",
		panelIds: ["grid", "chart"],
		storage: localStorage,
	});

	const columnDefs = useMemo(() => buildColumnDefs(), []);
	const rows = dataQuery.data?.rows ?? [];
	const total = dataQuery.data?.total ?? 0;
	const max = dataQuery.data?.max ?? 0;

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `Gas_Consumption_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				if (params.column.getColId() === "Value") {
					return params.value == null ? "" : Number(params.value).toFixed(2);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Gas Consumption
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<GasConsumptionToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onExportExcel={handleExportToExcel}
				/>
			</Paper>

			<GasConsumptionSummary total={total} max={max} />

			<Group
				id="gas-consumption-outer"
				orientation="horizontal"
				defaultLayout={outerLayout.defaultLayout ?? OUTER_LAYOUT}
				onLayoutChange={outerLayout.onLayoutChange}
				style={{ flex: 1, width: "100%", minHeight: 0 }}
			>
				<Panel id="grid" minSize={25} style={{ paddingBottom: 5 }}>
					<Paper
						elevation={3}
						sx={{ height: "100%", width: "100%", overflow: "hidden", pr: 0 }}
					>
						{dataQuery.isLoading ? (
							<CenteredLazyLoading />
						) : (
							<AgGridReact<GasRow>
								columnDefs={columnDefs}
								rowData={rows}
								rowModelType="clientSide"
								defaultColDef={{ resizable: true }}
								suppressContextMenu
								headerHeight={32}
								rowHeight={28}
								onGridReady={(e) => {
									gridApiRef.current = e.api;
								}}
							/>
						)}
					</Paper>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<ResizeGrip />
				</Separator>

				<Panel id="chart" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", p: 1 }}>
							{dataQuery.isLoading ? (
								<CenteredLazyLoading />
							) : (
								<GasConsumptionChart rows={rows} />
							)}
						</Paper>
					</Box>
				</Panel>
			</Group>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
